/**
 * Sponsor subscription state + checkout for the current org (NOS-170).
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { useOrgStore } from "@/stores/orgStore"
import {
  canAccessConsole,
  canWriteAsSponsor,
  effectiveSponsorStatus,
  type SponsorBillingFields,
  type SponsorSubscriptionStatus,
} from "@/lib/sponsorBilling"

export interface SponsorSubscription {
  loading: boolean
  /** Effective status with lazy grace expiry applied. */
  status: SponsorSubscriptionStatus
  graceExpiresAt: string | null
  isFoundingSponsor: boolean
  manualActive: boolean
  /** Console visible (active, unexpired grace, or manual flag). */
  canAccess: boolean
  /** Sponsor writes allowed (active or manual flag). */
  canWrite: boolean
  /** Redirects to Stripe Checkout on success. */
  startCheckout: () => Promise<{ error: string | null }>
  refresh: () => void
}

export function useSponsorSubscription(): SponsorSubscription {
  const { currentOrg } = useOrgStore()
  const [loading, setLoading] = useState(true)
  const [fields, setFields] = useState<SponsorBillingFields | null>(null)

  const fetchBilling = useCallback(async () => {
    if (!currentOrg?.id) {
      setFields(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const { data } = await supabase
      .from("organizations")
      .select(
        "sponsor_subscription_status, grace_expires_at, sponsor_manual_active, is_founding_sponsor"
      )
      .eq("id", currentOrg.id)
      .maybeSingle()
    setFields((data as SponsorBillingFields | null) ?? null)
    setLoading(false)
  }, [currentOrg?.id])

  useEffect(() => {
    fetchBilling()
  }, [fetchBilling])

  const startCheckout = useCallback(async (): Promise<{ error: string | null }> => {
    if (!currentOrg?.id) return { error: "No organization selected" }
    const { data: sess } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke("create-sponsor-subscription", {
      body: { org_id: currentOrg.id, origin: window.location.origin },
      ...(sess.session?.access_token
        ? { headers: { Authorization: `Bearer ${sess.session.access_token}` } }
        : {}),
    })
    if (error || !data?.url) {
      return { error: error?.message ?? "Failed to start checkout" }
    }
    window.location.assign(data.url as string)
    return { error: null }
  }, [currentOrg?.id])

  const safeFields: SponsorBillingFields = fields ?? {
    sponsor_subscription_status: "none",
    grace_expires_at: null,
    sponsor_manual_active: false,
    is_founding_sponsor: false,
  }

  return {
    loading,
    status: effectiveSponsorStatus(
      safeFields.sponsor_subscription_status,
      safeFields.grace_expires_at
    ),
    graceExpiresAt: safeFields.grace_expires_at,
    isFoundingSponsor: safeFields.is_founding_sponsor,
    manualActive: safeFields.sponsor_manual_active,
    canAccess: canAccessConsole(safeFields),
    canWrite: canWriteAsSponsor(safeFields),
    startCheckout,
    refresh: fetchBilling,
  }
}
